import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import { useNotificationSocket } from "../context/SocketContext";
import { NOTIFICATION_TYPES, timeAgo } from "../utils/notificationConfig";
import { getProfilePicture } from "../utils/avatar";
import { FaTimes, FaBell, FaArrowRight, FaChevronDown, FaChevronUp } from "react-icons/fa";
import api from "../services/api";

const NotificationPanel = ({ isOpen, onClose, desktop = false, mobile = false }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { condition, instanceId } = useContext(SurveyContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);

  // Fetch notifications when panel opens
  useEffect(() => {
    if (!isOpen) return;
    setClosing(false);
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const params = { limit: 30 };
        if (instanceId) params.instanceId = instanceId;
        else if (condition) params.condition = condition;
        const { data } = await api.get("/notifications", { params });
        setItems(Array.isArray(data) ? data : data.notifications || []);
      } catch (e) {
        console.error("Failed to load notifications:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [isOpen, condition, instanceId]);

  // Real-time updates
  const handleNewNotification = useCallback((notification) => {
    if (notification.actorId === user?.userId) return;
    setItems((prev) => [notification, ...prev]);
  }, [user?.userId]);

  useNotificationSocket(isOpen ? handleNewNotification : null);

  // Lock body scroll on mobile only
  useEffect(() => {
    if (isOpen && mobile) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isOpen, mobile]);

  // Close — with animation on mobile, instant on desktop
  const handleClose = useCallback(() => {
    if (desktop) {
      onClose();
    } else {
      setClosing(true);
      setTimeout(() => {
        setClosing(false);
        onClose();
      }, 200);
    }
  }, [onClose, desktop]);

  const markAllRead = async () => {
    try {
      const params = {};
      if (instanceId) params.instanceId = instanceId;
      else if (condition) params.condition = condition;
      await api.post("/notifications/read-all", params);
      setItems((prev) => prev.map((n) => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
      // Notify Navbar/BottomNav to reset their badge count immediately
      window.dispatchEvent(new CustomEvent("spiders:notifications-read"));
    } catch (e) {
      console.error("Failed to mark all read:", e);
    }
  };

  // Group notifications by targetId (thread)
  const grouped = useMemo(() => {
    const groups = [];
    const groupMap = new Map();

    items.forEach((n) => {
      const key = n.targetType === "message" ? (n.targetId || n._id) : n._id;
      if (n.targetType === "message" && groupMap.has(key)) {
        const idx = groupMap.get(key);
        groups[idx].notifications.push(n);
        if (new Date(n.createdAt) > new Date(groups[idx].lead.createdAt)) {
          groups[idx].lead = n;
        }
        if (!n.readAt) groups[idx].hasUnread = true;
      } else {
        groupMap.set(key, groups.length);
        groups.push({
          key,
          targetType: n.targetType,
          notifications: [n],
          lead: n,
          hasUnread: !n.readAt,
        });
      }
    });

    return groups;
  }, [items]);

  const handleNavigate = (n) => {
    handleClose();
    if (n.targetType === "message" && n.targetId) {
      const commentParam = n.sourceCommentId ? `&commentId=${n.sourceCommentId}` : "";
      navigate(`/messages/${n.targetId}?condition=${condition}${commentParam}`);
    } else if (n.targetType === "user") {
      navigate(`/profile/${n.actorUsername || n.actorId}?condition=${condition}`);
    }
  };

  if (!isOpen && !closing) return null;

  const unreadCount = items.filter((n) => !n.readAt).length;

  return (
    <>
      {/* Backdrop */}
      {mobile && (
        <div
          className={`fixed inset-0 z-[55] bg-black/50 lg:hidden ${closing ? "opacity-0" : "notification-backdrop-enter"}`}
          style={{ transition: closing ? "opacity 0.2s" : undefined }}
          onClick={handleClose}
        />
      )}
      {desktop && (
        <div
          className="fixed inset-0 z-[54] hidden lg:block"
          onClick={handleClose}
        />
      )}

      {/* Panel */}
      <div
        className={
          desktop
            ? `absolute left-full top-[120px] w-[400px] max-h-[60vh] rounded-xl border border-spiders-border shadow-2xl bg-spiders-bg-dark z-[55] flex-col hidden lg:flex`
            : `fixed right-0 top-0 bottom-0 w-full bg-spiders-bg-dark border-spiders-border z-[55] flex flex-col lg:hidden ${closing ? "notification-panel-exit" : "notification-panel-enter"}`
        }
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-spiders-border bg-spiders-bg-surface">
          <div className="flex items-center gap-2">
            <FaBell className="w-4 h-4 text-spiders-primary-400" />
            <h2 className="text-lg font-bold text-spiders-text-primary">Notifications</h2>
            {unreadCount > 0 && (
              <span className="bg-spiders-primary-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-spiders-primary-400 hover:text-spiders-primary-300 font-medium transition-colors"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={handleClose}
              className="p-1.5 rounded-full hover:bg-spiders-bg-elevated text-spiders-text-muted hover:text-spiders-text-primary transition-colors"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Notification list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-spiders-primary-400" />
            </div>
          ) : grouped.length === 0 ? (
            <div className="text-center py-12">
              <FaBell className="w-10 h-10 mx-auto mb-3 text-spiders-border" />
              <p className="text-spiders-text-muted">No notifications yet</p>
              <p className="text-spiders-text-muted text-xs mt-1">Interact with posts to see activity here</p>
            </div>
          ) : (
            grouped.map((group) => (
              <NotificationGroupItem
                key={group.key}
                group={group}
                onNavigate={handleNavigate}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-spiders-border p-3">
          <button
            onClick={() => { handleClose(); navigate("/notifications"); }}
            className="w-full flex items-center justify-center gap-2 text-sm text-spiders-primary-400 hover:text-spiders-primary-300 transition-colors py-2"
          >
            View all notifications <FaArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </>
  );
};

// Individual grouped notification item
const NotificationGroupItem = ({ group, onNavigate }) => {
  const { notifications, lead, hasUnread } = group;
  const config = NOTIFICATION_TYPES[lead.type] || NOTIFICATION_TYPES.comment;
  const Icon = config.icon;
  const [expanded, setExpanded] = useState(false);

  const uniqueActors = [...new Set(notifications.map((n) => n.actorUsername).filter(Boolean))];
  const othersCount = uniqueActors.length - 1;
  const isSingleNotification = notifications.length === 1;

  // Build verb text for the group header
  let verbText;
  if (isSingleNotification || uniqueActors.length <= 1) {
    verbText = config.verb;
    if (!isSingleNotification && uniqueActors.length === 1) {
      verbText += ` (${notifications.length}×)`;
    }
  } else if (uniqueActors.length === 2) {
    // Both names shown
    verbText = null; // handled in JSX below
  } else {
    verbText = `and ${othersCount} others ${config.verb.replace("your post", "a thread")}`;
  }

  const actorUsername = lead.actorUsername || "Someone";

  return (
    <div className={`border-b border-spiders-border/30 ${hasUnread ? "bg-spiders-primary-950/20" : ""}`}>
      {/* Group header */}
      <button
        onClick={() => isSingleNotification ? onNavigate(lead) : (expanded ? setExpanded(false) : onNavigate(lead))}
        className="w-full text-left p-4 hover:bg-spiders-bg-elevated/60 transition-colors"
      >
        <div className="flex items-start gap-3">
          {/* Avatar with icon badge */}
          <div className="relative flex-shrink-0">
            {uniqueActors.length > 1 ? (
              <div className="w-11 h-11 relative">
                <img
                  src={getProfilePicture(uniqueActors[1], null)}
                  alt=""
                  className="w-8 h-8 rounded-full absolute bottom-0 right-0 border-2 border-spiders-bg-dark"
                />
                <img
                  src={getProfilePicture(actorUsername, lead.actorAvatarSeed)}
                  alt={actorUsername}
                  className="w-8 h-8 rounded-full absolute top-0 left-0 border-2 border-spiders-bg-dark z-10"
                />
              </div>
            ) : (
              <img
                src={getProfilePicture(actorUsername, lead.actorAvatarSeed)}
                alt={actorUsername}
                className="w-10 h-10 rounded-full border border-spiders-border"
              />
            )}
            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${config.bgClass} flex items-center justify-center ring-2 ring-spiders-bg-dark z-20`}>
              <Icon className="w-2.5 h-2.5 text-white" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-spiders-text-primary leading-snug">
                {uniqueActors.length === 2 && !isSingleNotification ? (
                  // Two actors: both names in accent color
                  <>
                    <span className="font-bold text-spiders-primary-400">@{actorUsername}</span>
                    {" and "}
                    <span className="font-bold text-spiders-primary-400">@{uniqueActors[1]}</span>
                    {" "}{config.verb.replace("your post", "a thread")}
                  </>
                ) : (
                  <>
                    <span className="font-bold text-spiders-primary-400">@{actorUsername}</span>
                    {" "}{verbText}
                  </>
                )}
              </p>
              <span className="text-xs text-spiders-text-muted whitespace-nowrap flex-shrink-0 mt-0.5">
                {timeAgo(lead.createdAt)}
              </span>
            </div>
            {lead.contentPreview && (
              <p className="text-xs text-spiders-text-secondary mt-1 line-clamp-2 italic leading-relaxed">
                "{lead.contentPreview}"
              </p>
            )}
            <div className="flex items-center gap-3 mt-1.5">
              {hasUnread && (
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-spiders-primary-400 animate-pulse" />
                  <span className="text-xs text-spiders-primary-400 font-medium">New</span>
                </div>
              )}
              {/* Expand/collapse for grouped notifications */}
              {!isSingleNotification && (
                <button
                  onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
                  className="flex items-center gap-1 text-xs text-spiders-text-muted hover:text-spiders-text-secondary transition-colors"
                >
                  {expanded ? <FaChevronUp className="w-2.5 h-2.5" /> : <FaChevronDown className="w-2.5 h-2.5" />}
                  {expanded ? "Hide" : `Show ${notifications.length} comments`}
                </button>
              )}
            </div>
          </div>
        </div>
      </button>

      {/* Expanded individual notifications */}
      {expanded && !isSingleNotification && (
        <div className="border-t border-spiders-border/20">
          {notifications.map((n) => {
            const nConfig = NOTIFICATION_TYPES[n.type] || NOTIFICATION_TYPES.comment;
            const NIcon = nConfig.icon;
            return (
              <button
                key={n._id}
                onClick={() => onNavigate(n)}
                className={`w-full text-left pl-12 pr-4 py-2.5 hover:bg-spiders-bg-elevated/40 transition-colors border-b border-spiders-border/10 ${
                  !n.readAt ? "bg-spiders-primary-950/10" : ""
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <div className="relative flex-shrink-0">
                    <img
                      src={getProfilePicture(n.actorUsername || "unknown", n.actorAvatarSeed)}
                      alt={n.actorUsername}
                      className="w-7 h-7 rounded-full border border-spiders-border"
                    />
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ${nConfig.bgClass} flex items-center justify-center ring-1 ring-spiders-bg-dark`}>
                      <NIcon className="w-1.5 h-1.5 text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-spiders-text-primary">
                      <span className="font-semibold text-spiders-primary-400">@{n.actorUsername || "Someone"}</span>
                      {" "}{nConfig.verb}
                    </p>
                    {n.contentPreview && (
                      <p className="text-xs text-spiders-text-muted mt-0.5 line-clamp-1 italic">
                        "{n.contentPreview}"
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] text-spiders-text-muted whitespace-nowrap mt-0.5">
                    {timeAgo(n.createdAt)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
